import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import {
	CustomerInvoiceFormView,
	CustomerInvoicePaymentView,
} from 'app/models/miscellaneous-billing/CustomerInvoiceFormView';
import { RemainingOverpaymentView } from 'app/models/RemainingOverpaymentView';
import { RemainingDepositView } from 'app/models/RemainingDepositView';
import { CustomerFormView } from 'app/models/miscellaneous-billing/Customer-form';
import { Configuration } from 'app/models/Configuration';
import { setupAttachmentFormRequest } from './attachment-form.utils';

@Injectable({
	providedIn: 'root',
})
export class CustomerInvoiceFromService {
	private readonly urlPath = `${environment.apiUrl}miscellaneous-billing/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form`, setupAttachmentFormRequest(request))
			.pipe();
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}`, setupAttachmentFormRequest(request))
			.pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${
			this.urlPath
		}${clientId}/customer-invoice-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/customer-invoice-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/customer-invoice-form/${id}`).pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/customer-invoice-form/${formId}/docs/${docId}`);
	}
	submitForApproval(clientId: string, id: any, userId: string): Observable<CustomerInvoiceFormView> {
		return this.httpClient
			.post<CustomerInvoiceFormView>(`${this.urlPath}${clientId}/customer-invoice-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	search(clientId: string): Observable<CustomerInvoiceFormView[]> {
		return this.httpClient
			.get<CustomerInvoiceFormView[]>(`${this.urlPath}${clientId}/customer-invoice-form/search`)
			.pipe();
	}

	gridSearch(clientId: string, request: any): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/grid-search`, request).pipe();
	}

	generateInvoice(clientId: string, id: string): Observable<any> {
		let url = `${this.urlPath}${clientId}/customer-invoice-form/invoice/${id}`;
		return this.httpClient.get<any>(url).pipe();
	}

	getPaymentHistory(clientId: string, id: any): Observable<CustomerInvoicePaymentView[]> {
		return this.httpClient
			.get<CustomerInvoicePaymentView[]>(`${this.urlPath}${clientId}/customer-invoice-form/item/${id}`)
			.pipe();
	}

	itemPayment(clientId: string, request: CustomerInvoicePaymentView): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/item-payment`, request).pipe();
	}

	emailInvoice(clientId: string, id: string, attachmentIds?: string[]): Observable<any> {
		const body = {
			attachmentIds: attachmentIds || [],
		};
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${id}/email-invoice`, body)
			.pipe();
	}

	export(clientId: string) {
		return this.httpClient
			.get(`${this.urlPath}${clientId}/customer-invoice-form/export`, {
				responseType: 'blob',
			})
			.pipe();
	}

	getLastInvoice(clientId: string, customerId: string): Observable<CustomerInvoiceFormView> {
		return this.httpClient
			.get<CustomerInvoiceFormView>(`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/last-invoice`)
			.pipe();
	}

	addOverpaymentItem(clientId: string, formId: string, request: CustomerInvoicePaymentView): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${formId}/overpayment`, request)
			.pipe();
	}

	overpaymentbalance(clientId: string, customerId: string): Observable<RemainingOverpaymentView[]> {
		return this.httpClient
			.get<RemainingOverpaymentView[]>(
				`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/overpaymentbalance`
			)
			.pipe();
	}

	overpaymentbalanceforrefund(clientId: string, customerId: string): Observable<RemainingOverpaymentView[]> {
		return this.httpClient
			.get<RemainingOverpaymentView[]>(
				`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/overpaymentbalanceforrefund`
			)
			.pipe();
	}

	depositbalance(clientId: string, customerId: string): Observable<RemainingDepositView[]> {
		return this.httpClient
			.get<RemainingDepositView[]>(`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/depositbalance`)
			.pipe();
	}

	depositbalanceforrefund(clientId: string, customerId: string): Observable<RemainingDepositView[]> {
		return this.httpClient
			.get<RemainingDepositView[]>(
				`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/depositbalanceforrefund`
			)
			.pipe();
	}

	getcarryoveritems(clientId: string, customerId: string, formId: string): Observable<CustomerInvoiceFormView[]> {
		return this.httpClient
			.get<CustomerInvoiceFormView[]>(
				`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/${formId}/carryoveritems`
			)
			.pipe();
	}

	InvoicesWithOverpayment(clientId: string): Observable<CustomerFormView[]> {
		return this.httpClient
			.get<CustomerFormView[]>(`${this.urlPath}${clientId}/customer-invoice-form/invoiceswithoverpayment`)
			.pipe();
	}

	getActiveForms(clientId: string, customerId: string): Observable<CustomerInvoiceFormView[]> {
		return this.httpClient
			.get<CustomerInvoiceFormView[]>(`${this.urlPath}${clientId}/customer-invoice-form/${customerId}/activeinvoices`)
			.pipe();
	}
	getRecurringForms(clientId: string): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.urlPath}${clientId}/customer-invoice-form/recurring-forms`).pipe();
	}

	getCustomerInvoiceForScheduling(clientId: string, data: any): Observable<CustomerInvoiceFormView[]> {
		return this.httpClient
			.post<CustomerInvoiceFormView[]>(`${this.urlPath}${clientId}/customer-invoice-form/manage/scheduling`, data)
			.pipe();
	}

	updateCustomerInvoiceForScheduling(clientId: string, message: any): Observable<CustomerInvoiceFormView[]> {
		return this.httpClient
			.post<CustomerInvoiceFormView[]>(
				`${this.urlPath}${clientId}/customer-invoice-form/manage/scheduling/update`,
				message
			)
			.pipe();
	}

	generateSchedulePreview(clientId: string, formId: string, formData: CustomerInvoiceFormView): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/${formId}/generate-schedule-preview`, formData)
			.pipe();
	}

	// Attachment methods
	uploadAttachment(clientId: string, invoiceId: string, file: File, attachmentView: any): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		formData.append('attachmentView', JSON.stringify(attachmentView));

		const req = new HttpRequest(
			'POST',
			`${this.urlPath}${clientId}/customer-invoice-form/${invoiceId}/attachments`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	updateAttachment(clientId: string, invoiceId: string, attachmentId: string, attachmentView: any): Observable<any> {
		return this.httpClient
			.put<any>(
				`${this.urlPath}${clientId}/customer-invoice-form/${invoiceId}/attachments/${attachmentId}`,
				attachmentView
			)
			.pipe();
	}

	deleteAttachment(clientId: string, invoiceId: string, attachmentId: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${this.urlPath}${clientId}/customer-invoice-form/${invoiceId}/attachments/${attachmentId}`)
			.pipe();
	}

	getAutoInvoiceConfiguration(clientId: string): Observable<Configuration> {
		return this.httpClient
			.get<Configuration>(`${this.urlPath}${clientId}/customer-invoice-form/auto-toggle-configuration`)
			.pipe();
	}

	updateInvoiceField(
		clientId: string,
		invoiceId: string,
		fieldName: string,
		value: any
	): Observable<CustomerInvoiceFormView> {
		const request = {
			fieldName: fieldName,
			value: value,
		};
		return this.httpClient
			.patch<CustomerInvoiceFormView>(`${this.urlPath}${clientId}/customer-invoice-form/${invoiceId}/field`, request)
			.pipe();
	}

	getTransactionStatus(clientId: string, paymentId: number): Observable<any> {
		return this.httpClient
			.get<any>(`${this.urlPath}${clientId}/customer-invoice-form/payment/${paymentId}/transaction-status`)
			.pipe();
	}

	refundPayment(
		clientId: string,
		paymentId: number,
		request: { amount: number; refundType: string; reason: string }
	): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/payment/${paymentId}/refund`, request)
			.pipe();
	}

	voidPayment(clientId: string, paymentId: number, request: { reason: string }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/customer-invoice-form/payment/${paymentId}/void`, request)
			.pipe();
	}
}
